<template>
  <div class="deal-customer">
    <p v-if="!lead" class="deal-customer__state">
      {{ $t('deals.customer_history.no_customer') }}
    </p>

    <template v-else>
      <div class="deal-customer__card">
        <div class="deal-customer__avatar">{{ initials }}</div>
        <div class="deal-customer__identity">
          <span class="deal-customer__name">{{ customerName }}</span>
          <span class="deal-customer__meta">
            <template v-if="lead.email">{{ lead.email }}</template>
            <template v-if="lead.email && lead.phone"> · </template>
            <template v-if="lead.phone">{{ lead.phone }}</template>
          </span>
          <span v-if="lead.created_at" class="deal-customer__meta">
            {{ $t('deals.customer_history.customer_since', { date: formatDate(lead.created_at) }) }}
          </span>
        </div>
        <div class="deal-customer__card-actions">
          <Btn
            v-if="lead.email"
            type="button"
            variant="outlined"
            size="sm"
            icon="mail"
            :text="$t('deals.mail_compose')"
            @click="$emit('email-customer')"
          />
          <Btn
            type="button"
            variant="outlined"
            size="sm"
            icon="arrow-up-right"
            :text="$t('deals.customer_history.open_card')"
            @click="$emit('open-customer')"
          />
        </div>
      </div>

      <p v-if="loading" class="deal-customer__state">{{ $t('common.loading') }}</p>

      <template v-else>
        <section class="deal-customer__group">
          <header class="deal-customer__group-header">
            <span class="deal-customer__group-label">
              {{ $t('deals.customer_history.requests') }}
            </span>
            <span class="deal-customer__group-count">{{ submissionTotal }}</span>
          </header>
          <p v-if="!submissions.length" class="deal-customer__empty">
            {{ $t('deals.customer_history.no_requests') }}
          </p>
          <div
            v-for="submission in visibleSubmissions"
            :key="submission.id"
            role="button"
            tabindex="0"
            class="deal-customer__row"
            @click="openSubmission(submission)"
            @keydown.enter.self="openSubmission(submission)"
          >
            <BaseIcon name="inbox" size="14" class="deal-customer__row-icon" />
            <span class="deal-customer__row-main">
              <span class="deal-customer__row-title">
                {{ submission.form?.name || $t('deals.customer_history.unknown_form') }}
              </span>
              <span v-if="submissionPreview(submission)" class="deal-customer__row-sub">
                {{ submissionPreview(submission) }}
              </span>
            </span>
            <span class="deal-customer__row-aside">{{ formatDate(submission.created_at) }}</span>
          </div>
          <button
            v-if="submissions.length > PREVIEW_SIZE"
            type="button"
            class="deal-customer__toggle"
            @click="toggleSection('submissions')"
          >
            {{
              expanded.submissions
                ? $t('journey.actions.show_less')
                : $t('journey.actions.show_all', { count: submissions.length })
            }}
          </button>
        </section>

        <section class="deal-customer__group">
          <header class="deal-customer__group-header">
            <span class="deal-customer__group-label">
              {{ $t('deals.customer_history.deals') }}
            </span>
            <span class="deal-customer__group-count">{{ deals.length }}</span>
          </header>
          <p v-if="deals.length <= 1" class="deal-customer__empty">
            {{ $t('deals.customer_history.no_other_deals') }}
          </p>
          <div
            v-for="deal in visibleDeals"
            :key="deal.id"
            :role="deal.id === dealId ? undefined : 'button'"
            :tabindex="deal.id === dealId ? undefined : 0"
            class="deal-customer__row"
            :class="{ 'deal-customer__row--current': deal.id === dealId }"
            @click="openDeal(deal)"
            @keydown.enter.self="openDeal(deal)"
          >
            <span
              class="deal-customer__stage-dot"
              :style="{ backgroundColor: deal.stage?.color || '#8a93a8' }"
            ></span>
            <span class="deal-customer__row-main">
              <span class="deal-customer__row-title">
                {{ deal.title || $t('deals.untitled_deal') }}
              </span>
              <span class="deal-customer__row-sub">
                {{ deal.stage?.name || '-' }} · {{ formatDate(deal.created_at) }}
              </span>
            </span>
            <span v-if="deal.id === dealId" class="deal-customer__current">
              {{ $t('deals.customer_history.current_deal') }}
            </span>
            <span class="deal-customer__row-aside">{{ formatCurrency(deal.value) }}</span>
          </div>
          <button
            v-if="deals.length > PREVIEW_SIZE"
            type="button"
            class="deal-customer__toggle"
            @click="toggleSection('deals')"
          >
            {{
              expanded.deals
                ? $t('journey.actions.show_less')
                : $t('journey.actions.show_all', { count: deals.length })
            }}
          </button>
        </section>
      </template>
    </template>
  </div>
</template>

<script>
  import { ref, computed, watch, getCurrentInstance } from 'vue';
  import { crmService } from '@/services/crmService';
  import { dealService } from '@/services/dealService';
  import { useFormStore } from '@/store/formStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';
  import Btn from '@/components/partials/Btn.vue';
  import SubmissionDetails from '@/components/crm/submissionDetails.vue';

  import BaseIcon from '@shared/components/BaseIcon.vue';

  const PREVIEW_SIZE = 5;

  export default {
    name: 'DealCustomerHistory',

    components: { Btn, BaseIcon },

    props: {
      lead: {
        type: Object,
        default: null,
      },
      dealId: {
        type: String,
        required: true,
      },
    },

    emits: ['open-customer', 'open-deal', 'email-customer'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const formStore = useFormStore();
      const toastStore = useToastStore();
      const uiStore = useUIStore();

      const loading = ref(false);
      const submissions = ref([]);
      const submissionTotal = ref(0);
      const deals = ref([]);
      const expanded = ref({ submissions: false, deals: false });

      const visibleSubmissions = computed(() =>
        expanded.value.submissions ? submissions.value : submissions.value.slice(0, PREVIEW_SIZE)
      );
      const visibleDeals = computed(() =>
        expanded.value.deals ? deals.value : deals.value.slice(0, PREVIEW_SIZE)
      );

      const toggleSection = (key) => {
        expanded.value = { ...expanded.value, [key]: !expanded.value[key] };
      };

      const customerName = computed(() => {
        const lead = props.lead;
        if (!lead) return '';
        return (
          [lead.first_name, lead.last_name].filter(Boolean).join(' ') ||
          lead.name ||
          lead.company_name ||
          lead.email ||
          ''
        );
      });

      const initials = computed(() => {
        const parts = customerName.value.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return '?';
        return parts
          .slice(0, 2)
          .map((part) => part.charAt(0))
          .join('')
          .toUpperCase();
      });

      const fetchHistory = async (leadId) => {
        expanded.value = { submissions: false, deals: false };
        if (!leadId) {
          submissions.value = [];
          submissionTotal.value = 0;
          deals.value = [];
          return;
        }
        loading.value = true;
        try {
          const [submissionResponse, dealResponse] = await Promise.all([
            crmService.getLeadSubmissions(leadId, { page: 1, limit: 50 }),
            dealService.getDealsByLead(leadId),
          ]);
          submissions.value = submissionResponse?.submissions || [];
          submissionTotal.value =
            submissionResponse?.pagination?.total ?? submissions.value.length;
          deals.value = [...(dealResponse?.deals || [])].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('deals.customer_history.load_failed'),
          });
        } finally {
          loading.value = false;
        }
      };

      watch(() => props.lead?.id, fetchHistory, { immediate: true });

      const submissionPreview = (submission) => {
        const fields = Array.isArray(submission.preview_data) ? submission.preview_data : [];
        return fields
          .slice(0, 2)
          .map((field) => field.value)
          .filter(Boolean)
          .join(' · ');
      };

      const openSubmission = async (submission) => {
        let form = submission.form;
        if (!form?.pages) {
          const formId = submission.form_id || submission.form?.id;
          if (formId) {
            try {
              await formStore.fetchFormById(formId);
              form = formStore.currentForm;
            } catch (error) {
              console.warn('Could not load form:', error);
            }
          }
        }
        uiStore.addModal(SubmissionDetails, {
          title: $t('submissions.submission_details'),
          submission,
          form: form || null,
          size: 'xxl',
        });
      };

      const openDeal = (deal) => {
        if (deal.id === props.dealId) return;
        emit('open-deal', deal);
      };

      const formatDate = (value) =>
        value
          ? new Date(value).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })
          : '';

      const formatCurrency = (value) =>
        new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);

      return {
        PREVIEW_SIZE,
        loading,
        submissions,
        submissionTotal,
        visibleSubmissions,
        deals,
        visibleDeals,
        expanded,
        toggleSection,
        customerName,
        initials,
        submissionPreview,
        openSubmission,
        openDeal,
        formatDate,
        formatCurrency,
      };
    },
  };
</script>

<style scoped lang="scss">
  .deal-customer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;

    &__state {
      margin: 0;
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;
    }

    &__card-actions {
      display: flex;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 8px;
    }

    &__avatar {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #e6e9ff;
      color: #3364e6;
      font-size: 13px;
      font-weight: 500;
    }

    &__identity {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    &__name {
      color: #000921;
      font-size: 14px;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__meta {
      color: #434f6f;
      font-size: 12px;
      font-weight: 300;
      @include ellipsis(1);
    }

    &__group {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 10px;
      border-radius: 6px;
      background: #f6f7fa;
    }

    &__group-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 2px 4px 6px;
    }

    &__group-label {
      color: #000921;
      font-size: 13px;
      font-weight: 500;
    }

    &__group-count {
      color: #434f6f;
      font-size: 13px;
      font-weight: 300;
    }

    &__empty {
      margin: 0;
      padding: 0 4px 4px;
      color: #434f6f;
      font-size: 12px;
      font-weight: 300;
    }

    &__row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border: 1px solid #e3e6ee;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;

      @include hover {
        border-color: #d7d7d7;
      }

      &:focus-visible {
        outline: 2px solid #3364e6;
        outline-offset: 2px;
      }

      &--current {
        cursor: default;
        background: #fbfbfd;
      }
    }

    &__row-icon {
      flex-shrink: 0;
      color: #8a93a8;
    }

    &__stage-dot {
      flex-shrink: 0;
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    &__row-main {
      display: flex;
      flex: 1;
      flex-direction: column;
      min-width: 0;
    }

    &__row-title {
      color: #000921;
      font-size: 13px;
      font-weight: 500;
      @include ellipsis(1);
    }

    &__row-sub {
      color: #434f6f;
      font-size: 11px;
      font-weight: 300;
      @include ellipsis(1);
    }

    &__row-aside {
      flex-shrink: 0;
      color: #434f6f;
      font-size: 11px;
      font-weight: 300;
    }

    &__toggle {
      align-self: flex-start;
      margin-top: 4px;
      padding: 2px 4px;
      border: 0;
      background: transparent;
      color: #3364e6;
      font: inherit;
      font-size: 12px;
      cursor: pointer;
    }

    &__current {
      flex-shrink: 0;
      padding: 1px 6px;
      border-radius: 4px;
      background: #e6e9ff;
      color: #3364e6;
      font-size: 11px;
      font-weight: 500;
      line-height: 16px;
    }
  }
</style>
